import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { Add, Refresh } from '@material-ui/icons';
import { hasAtLeastOnePermission, getDimensionsOfPagination } from '../../../sc-utilities';
import MaterialTable from 'material-table';
import { hasPermission, localization } from '../../../sc-utilities';
import * as actions from '../store/customerActions';
import { MuiTableRows } from '../../../../themes';
import { CancelToken } from 'axios';
import DeliveryNoteCreate from './DeliveryNoteCreate';
import moment from 'moment';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const DeliveryNotesList = props => {
    const { classes, fetchDeliveryNotesCount, deliveryNotes, fetchDeliveryNotes, tableColumns, permissions, createDeliveryNote, activeCustomer, saveColumnsFilter, account, translations, tableRef } = props;

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);

    const [deleteModalState, setDeleteModalState] = useState({ open: false, addOpen: false, deliveryNoteIDs: [] });
    const countSource = CancelToken.source();
    const listSource = CancelToken.source();
    const isMounted = React.useRef(true);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        filterCellStyle: { maxWidth: '200px' },
        selection: hasAtLeastOnePermission(permissions, ['customer_delete']),
        search: false,
        minBodyHeight: window.innerHeight - 320 < 300 ? 300 : window.innerHeight - 320,
        maxBodyHeight: window.innerHeight - 320,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const handleOpenClose = () => {
        setDeleteModalState({ ...deleteModalState, addOpen: !deleteModalState.addOpen })
    }
    useEffect(() => {
        props.loading && tableRef.current && tableRef.current.onQueryChange();
        isMounted.current = true;
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
            isMounted.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.loading]);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };
    async function fetchTableData(query) {
        if (isMounted.current) {
            let q = filterQuery(query?.filters);

            q['CreatedAt'] = q['BilledDate'] ? moment(q['BilledDate']).set({ hour: 0, minute: 0, second: 0 }).format('YYYY-MM-DD HH:mm:ss') : null;
            query['ActiveCustomerID'] = activeCustomer;
            query['q'] = q;
            let count = await fetchDeliveryNotesCount(query, { countSource: countSource });

            if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
                let dimensions = getDimensionsOfPagination(count?.data)
                query['pageSize'] = dimensions.pageSize
                rowsPageSizeInit = dimensions.rowsPageSize
                pageSize = dimensions.pageSize
            }

            let result = count?.data ? await fetchDeliveryNotes(query, { listSource: listSource }) : { data: [] };
            isMounted.current && setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
            return { data: result?.data, page: query?.page, pageSize: pageSize, totalCount: count?.data };
        }
    }


    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                <Typography className={classes.typography}>{translations['delivery_notes'] || 'Delivery notes'}</Typography>
                {hasPermission(permissions, 'delivery_notes_create') ? <Fab
                    id="create_delivery_notes"
                    variant="extended"
                    disabled={account.length <= 0}
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={handleOpenClose}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} disabled={deliveryNotes.fetching} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };


    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`Customer`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer });
        return newColumnState;
    }

    return (
        <>
            <DeliveryNoteCreate
                account={account}
                translations={translations}
                handleOpen={handleOpenClose}
                createDeliveryNote={createDeliveryNote}
                creating={deliveryNotes.creating}
                open={deleteModalState.addOpen}
            />
            <MaterialTable
                style={{ marginLeft: ' 0.43rem', marginRight: '0.43rem', marginTop: '-0.47rem', marginBottom: '-2rem' }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    setTableOptions({ ...tableOptions, pageSize: size })
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
        </>
    );
};

const mapStateToProps = state => {
    return {
        translations: state?.translate?.translations?.activeTranslations || {},
        permissions: state?.core?.login?.permissions || [],
        deleting: state?.customer?.customers?.deleting
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onBulkDeleteCustomers: params => dispatch(actions.bulkDeleteCustomers(params)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(DeliveryNotesList));